import { Controller } from "@hotwired/stimulus"

// Cierre del modal V2 SIN request. La X y Cancelar siguen siendo links a
// close_url (el fallback natural: sin JS, o cuando el modal ES la página),
// pero en contexto overlay el click se intercepta y solo se descarta el
// contenido del frame. Cerrar un modal no debe costar un GET de la página
// entera — era el "close navega a close_url" que re-renderizaba el tablero.
//
// Dónde corre este controller: el `.tp-modal` vive dentro de un
// `<turbo-frame id=frame_id>`. Dos contextos, decididos por el `src` del frame:
//
// - Overlay: el modal llegó al frame por navegación de link y Turbo le seteó
//   `src` (ver #navigateFrame de Turbo). Hay página atrás → descartar y listo.
// - Página suelta: un 422 del form (que rompe el frame con `_top`) re-renderiza
//   new.slim como página completa, sin `src` en el frame. No hay nada atrás →
//   NO se intercepta: el link navega a close_url y vuelve al listado.
//
// Cierran la X, Cancelar (data-action en esos links) y la tecla Escape, que
// pasa por el mismo link de la X para que overlay y página suelta se resuelvan
// igual. Escape se IGNORA con el formulario sucio: Chrome manda un keydown de
// Escape a la página al descartar su dropdown de autorrelleno, y eso cerraba
// el modal en plena escritura (v2-tables-modals.md). "Sucio" se decide en el
// momento de la tecla contra los defaults del HTML (defaultValue /
// defaultSelected / defaultChecked), sin estado en el controller: el morphing
// puede reutilizar el elemento sin reconectarlo. El backdrop sigue sin cerrar.
// El estado visible sigue siendo "está renderizado": este controller no pone
// clases, solo vacía el frame.
export default class extends Controller {
  // El modal se MUDA al elemento que está en pantalla completa. Sin esto se
  // carga y no se puede usar: el fullscreen mete a su elemento en el top layer
  // y vuelve INERTE todo lo que quede fuera de ese subárbol — no recibe clicks,
  // no acepta foco, y el teclado sigue yendo al botón que abrió el modal. Los
  // modales de la página del equipo cuelgan de frames que viven fuera del
  // bloque de gráficos, que es justo el que se va a pantalla completa.
  //
  // Promoverlo al top layer con la Popover API NO alcanza: el popover se abre y
  // se ve, pero su nodo sigue fuera del subárbol y la inercia se le aplica
  // igual (medido: `showPopover()` abre, `focus()` no mueve el foco y el
  // hit-test no llega). Lo que arregla las tres cosas es que el nodo esté
  // DENTRO del elemento en pantalla completa.
  //
  // Se muda el turbo-frame entero, no el `.tp-modal`: así `#close` sigue
  // encontrando su frame con `closest`, y Turbo lo sigue resolviendo por id
  // desde donde sea que esté.
  connect() {
    this.frame = this.element.closest("turbo-frame")
    this.adoptIntoFullscreen()

    // Al salir de pantalla completa el frame vuelve a su sitio: si se quedara
    // adentro, el DOM dejaría de coincidir con el que sirve el servidor.
    this._onFullscreenChange = () => {
      if (!document.fullscreenElement) this.restoreFrame()
    }
    document.addEventListener("fullscreenchange", this._onFullscreenChange)

    // En document y no en el modal: Escape debe cerrar aunque el foco haya
    // quedado fuera (p. ej. en el botón que lo abrió).
    this._onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this._onKeydown)

    this.focusFirstField()
  }

  disconnect() {
    document.removeEventListener("fullscreenchange", this._onFullscreenChange)
    document.removeEventListener("keydown", this._onKeydown)
    // Mudar el frame desconecta y reconecta este controller. Solo se restaura
    // cuando el modal se fue de verdad (frame vaciado por `#close`): si no,
    // mover y restaurar se llamarían en círculo.
    if (!this.element.isConnected) this.restoreFrame()
  }

  adoptIntoFullscreen() {
    const stage = document.fullscreenElement
    if (!stage || !this.frame || stage.contains(this.frame)) return

    // El ancla se guarda en el frame y no en el controller: mudarlo recrea esta
    // instancia, y una referencia en `this` se perdería en el camino.
    this.frame.tpModalHome ||= { parent: this.frame.parentNode, next: this.frame.nextSibling }
    stage.appendChild(this.frame)
  }

  restoreFrame() {
    const home = this.frame?.tpModalHome
    if (!home) return

    this.frame.tpModalHome = null
    home.parent.insertBefore(this.frame, home.next)
  }

  // El foco entra al modal. Bajo pantalla completa se quedaba en el botón que
  // lo abrió —que está en otro subárbol— y el teclado seguía yendo ahí: se veía
  // el formulario y no se podía escribir en él. Fuera de pantalla completa
  // corresponde igual: un modal que deja el foco atrás obliga a tabular hasta
  // él.
  //
  // `preventScroll` porque el cuerpo del modal puede desbordar: enfocar el
  // primer campo no debe saltar el scroll a mitad del formulario.
  focusFirstField() {
    // Si el foco ya está adentro (esta conexión es la de la mudanza, no la
    // primera), no se toca: reenfocar movería el cursor de quien está tecleando.
    if (this.element.contains(document.activeElement)) return

    const field = this.element.querySelector(
      ".tp-modal__body :is(input, select, textarea, button, a[href]):not([type=hidden]):not([disabled])"
    )
    const fallback = this.element.querySelector(".tp-modal__dialog")

    if (field) {
      field.focus({ preventScroll: true })
    } else if (fallback) {
      // Sin campos (un modal de confirmación) el foco va al diálogo. `tabindex`
      // -1 lo hace enfocable por JS sin meterlo en el orden de tabulación.
      fallback.setAttribute("tabindex", "-1")
      fallback.focus({ preventScroll: true })
    }
  }

  // Escape = click en la X: así el cierre por teclado hereda la decisión
  // overlay/página suelta de #close sin duplicarla. Un confirm abierto encima
  // (v2--confirm) se lleva el Escape primero y lo detiene.
  onKeydown(event) {
    if (event.defaultPrevented) return
    if (!this.topmost()) return
    if (event.key === "Tab") return this.trapTab(event)
    if (event.key !== "Escape") return
    if (this.dirty()) return

    event.preventDefault()
    this.element.querySelector(".tp-modal__close")?.click()
  }

  // Tab y Shift+Tab circulan dentro del diálogo (campos, botones, la X): el
  // foco nunca cae a la página de atrás, que sigue en el DOM bajo el velo.
  // Sin esto, tabular tras el último botón se iba a la tabla del fondo y el
  // modal quedaba abierto pero fuera de alcance. Un confirm abierto encima
  // (v2--confirm) se lleva la tecla antes, en captura.
  trapTab(event) {
    const focusables = Array.from(this.element.querySelectorAll(
      "a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
    )).filter((el) => el.getClientRects().length > 0)
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    const inside = this.element.contains(active)

    if (event.shiftKey && (active === first || !inside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault()
      first.focus()
    }
  }

  // Con dos modales apilados (el "agregar al filtro" sobre el detalle de una
  // telemetría) Escape cierra solo el de arriba: el último `.tp-modal` del
  // DOM, que es el que se ve encima. Sin estado: se mira el DOM al momento.
  topmost() {
    const modals = document.querySelectorAll(".tp-modal")
    return modals[modals.length - 1] === this.element
  }

  // Sin estado: se compara cada campo contra el default que trajo el HTML.
  // Los hidden no cuentan (los pone el server, nadie los tocó).
  dirty() {
    const fields = this.element.querySelectorAll(
      ".tp-modal__body :is(input, select, textarea):not([type=hidden]):not([disabled])"
    )
    return Array.from(fields).some((field) => {
      if (field instanceof HTMLSelectElement) {
        // Un select sin `selected` en el HTML arranca en la primera opción:
        // ese es su default, no "ninguna".
        const defaultIndex = Array.from(field.options).findIndex((option) => option.defaultSelected)
        return field.selectedIndex !== Math.max(defaultIndex, 0)
      }
      if (field.type === "checkbox" || field.type === "radio") return field.checked !== field.defaultChecked
      return field.value !== field.defaultValue
    })
  }

  close(event) {
    const frame = this.element.closest("turbo-frame")

    if (!frame?.hasAttribute("src")) return // página suelta (422): que el link navegue

    event.preventDefault() // Turbo ignora clicks con defaultPrevented: cero requests
    frame.removeAttribute("complete")
    frame.removeAttribute("src")
    frame.replaceChildren()
  }
}
